// SubagentCard: an `Agent` tool call drawn as the sub-agent it launched
// (Chief of Staff mode). The reducer routes every event the sub-agent produced
// into `toolCall.subagent` (agentEventReducer, `parentToolUseId`), so this card
// only draws state: no fetching, no second reconstruction.
//
// One line (plan design/2026-10-26-chief-of-staff-agents-panel.md, T8): which
// agent, on what task, how it is going, its latest summary and, once it ends,
// its usage. It no longer expands: the sub-agent's tools and words are in the
// read-only drawer a click opens (DelegatedAgentsContext `openAgent`). Outside
// AgentChat there is no provider and the card is not a button. Shapes come
// from surface.ts: the card is a 12px card.

import React, { memo } from 'react'
import { Box, Typography } from '@mui/material'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import type { DelegatedStatus, DelegatedUsage, SubagentInfo, ToolCallInfo } from '../types.js'
import { useDelegatedAgents } from './DelegatedAgentsContext.js'
import { CARD_SX } from './surface.js'

/** The word a sub-agent's or workflow's status reads as. */
export function delegatedStatusLabel(status: DelegatedStatus): string {
  switch (status) {
    case 'running': return 'Working'
    case 'completed': return 'Done'
    case 'failed': return 'Failed'
    case 'stopped': return 'Stopped'
  }
}

/** The theme colour of a status: blue at work, green done, red failed, grey stopped. */
export function delegatedStatusColor(status: DelegatedStatus): string {
  switch (status) {
    case 'running': return 'info.main'
    case 'completed': return 'success.main'
    case 'failed': return 'error.main'
    case 'stopped': return 'text.disabled'
  }
}

function formatTokens(n: number): string {
  if (n < 1000) return `${n}`
  const k = n / 1000
  return `${k >= 10 ? Math.round(k) : Number(k.toFixed(1))}k`
}

function formatDuration(ms: number): string {
  const total = Math.max(1, Math.round(ms / 1000))
  if (total < 60) return `${total}s`
  const m = Math.floor(total / 60)
  const s = total % 60
  return s ? `${m}m ${s}s` : `${m}m`
}

/** "1.2k tokens · 1 tool · 6s": the task usage the SDK reports. */
export function formatUsage(usage: DelegatedUsage): string {
  const tools = `${usage.tool_uses} ${usage.tool_uses === 1 ? 'tool' : 'tools'}`
  return `${formatTokens(usage.total_tokens)} tokens · ${tools} · ${formatDuration(usage.duration_ms)}`
}

/** A status dot and its word. The dot pulses while the work runs. */
export function DelegatedStatusBadge({ status }: { status: DelegatedStatus }) {
  const color = delegatedStatusColor(status)
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
      <Box
        component="span"
        data-testid="delegated-status-dot"
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: color,
          ...(status === 'running' && {
            animation: 'delegatedPulse 1.2s ease-in-out infinite',
            '@keyframes delegatedPulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.35 },
            },
          }),
        }}
      />
      <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color }}>
        {delegatedStatusLabel(status)}
      </Typography>
    </Box>
  )
}

/** The sub-tree to draw: the reducer's, or one read from the call's input. */
function subagentOf(toolCall: ToolCallInfo): SubagentInfo {
  if (toolCall.subagent) return toolCall.subagent
  const input = toolCall.input || {}
  return {
    agentType: typeof input.subagent_type === 'string' ? input.subagent_type : undefined,
    description: typeof input.description === 'string' ? input.description : undefined,
    status: toolCall.status === 'running' ? 'running'
      : toolCall.status === 'error' ? 'failed'
      : toolCall.status === 'cancelled' ? 'stopped'
      : 'completed',
    text: '',
    toolCalls: [],
  }
}

interface DelegatedLineProps {
  testId: string
  /** Absent outside AgentChat: the line is then not a button. */
  onClick?: () => void
  icon: React.ReactNode
  kind: string
  title: string
  status: DelegatedStatus
  summary?: string
  /** Shown before the summary, e.g. "1 of 3 agents". */
  progress?: string
  usage?: DelegatedUsage
}

const ellipsis = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const

/**
 * The one line SubagentCard and WorkflowCard share: icon, kind, title,
 * progress and summary, usage once finished, status. A button only when
 * there is somewhere to go (inside AgentChat).
 */
export function DelegatedLine({ testId, onClick, icon, kind, title, status, summary, progress, usage }: DelegatedLineProps) {
  const finished = status !== 'running'
  const detail = [progress, summary].filter(Boolean).join(' · ')
  return (
    <Box
      {...(onClick ? { component: 'button' as const, type: 'button' as const, onClick } : {})}
      data-testid={testId}
      sx={{
        width: '100%',
        p: '8px 12px',
        backgroundColor: 'transparent',
        border: 'none',
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        ...(onClick && {
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'info.main', outlineOffset: -2 },
        }),
      }}
    >
      <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary', flexShrink: 0 }}>{icon}</Box>
      <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}>
        {kind}
      </Typography>
      <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, flexShrink: 1, ...ellipsis }}>
        {title}
      </Typography>
      {detail && (
        <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary', flex: 1, ...ellipsis }}>
          {detail}
        </Typography>
      )}
      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {finished && usage && (
          <Typography component="span" sx={{ fontSize: 12, color: 'text.disabled', whiteSpace: 'nowrap' }}>
            {formatUsage(usage)}
          </Typography>
        )}
        <DelegatedStatusBadge status={status} />
      </Box>
    </Box>
  )
}

function SubagentCard({ toolCall }: { toolCall: ToolCallInfo }) {
  const api = useDelegatedAgents()
  const sub = subagentOf(toolCall)
  const toolCallId = toolCall.id

  return (
    <Box data-testid="subagent-card" data-state={sub.status} sx={{ ...CARD_SX, mt: 1 }}>
      <DelegatedLine
        testId="subagent-card-header"
        onClick={api ? () => api.openAgent({ kind: 'subagent', toolCallId }) : undefined}
        icon={<SmartToyOutlinedIcon sx={{ fontSize: 16 }} />}
        kind="Sub-agent"
        title={sub.description || sub.agentType || 'Sub-agent'}
        status={sub.status}
        summary={sub.summary}
        usage={sub.usage}
      />
    </Box>
  )
}

export default memo(SubagentCard)
